import dynamic from 'next/dynamic';
import React, { useState } from 'react';

import { useIsBokeh } from '@vimeo/core/hooks/use-is-bokeh';
import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import { useViewer } from '@vimeo/viewer';

import { UserProfileMenuBokehStyles } from './UserProfileMenuBokehStyles';

const ContentFilterModal = dynamic(
  async () => (await import('@vimeo/ui/core/SettingsModal/ContentFilterModal')).ContentFilterModal,
);
const LanguageSelectModal = dynamic(
  async () =>
    (await import('@vimeo/ui/core/SettingsModal/LanguageSelectModal')).LanguageSelectModal,
);
const UpsellModal = dynamic(() => import('@vimeo/upsell-modal'));

export type ModalType = 'content_viewing_prefs' | 'language' | 'upsell' | null;

interface Props {
  children: (context: {
    setModal: React.Dispatch<React.SetStateAction<ModalType>>;
  }) => React.ReactNode;
}

/**
 * A provider component that encapsulates all the modals from the Top Nav.
 */
export const ModalProvider = (props: Props) => {
  const isBokeh = useIsBokeh();
  const viewer = useViewer();
  const [modal, setModal] = useState<ModalType>(null);

  return (
    <>
      {/** Below will be removed one this menu is built in Bokeh components */}
      <UserProfileMenuBokehStyles isBokeh={isBokeh} />
      {props.children({ setModal })}
      {viewer?.contentViewingPrefs && (
        <ContentFilterModal
          active={modal === 'content_viewing_prefs'}
          activeSet={(m) => setModal(m as ModalType)}
          token={viewer.xsrft}
          contentViewingPrefs={viewer.contentViewingPrefs}
        />
      )}
      {modal === 'language' && (
        <LanguageSelectModal
          active
          activeSet={(m) => setModal(m as ModalType)}
          locale={viewer?.locale}
          locales={viewer?.locales}
          localeLabels={viewer?.localeLabels}
          token={viewer?.xsrft}
        />
      )}
      {modal === 'upsell' && (
        <UpsellModal
          apiUrl={viewer?.apiUrl}
          onClose={() => setModal(null)}
          templateType="default"
          tracking={{
            params: {
              feature: 'teams',
              location: 'top_navigation_bar',
              page: getPageNameFromPath(window.location.pathname, viewer?.user) || 'ss_lihp',
              // For now, start_team is the only upsell opening a modal in the top nav
              upsell_name: 'start_team',
              integration: 'none',
            },
            paywallTracking: {
              paywallTrigger: 'top_navigation_account_menu_start_team_button',
              paywallLocation: 'top_navigation',
              paywallType: 'popup',
              paywallFeature: 'teams',
            },
          }}
          userConfig={{ jwt: viewer?.jwt, userId: viewer?.user?.id }}
          modalConfig={{
            mkcCode: 'unified-top-nav-legacy',
          }}
        />
      )}
    </>
  );
};
